/**
 * Header Component
 * Displays logo and navigation
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { usePortal } from '../context/PortalContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const { branding, isSectionVisible, hasPreviewAccess } = usePortal();
  const { isAuthenticated } = useCustomerAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navLinks = [
    { path: '/', label: 'Home', always: true },
    { path: '/products', label: 'Products', section: 'products' },
    { path: '/design', label: 'Design Your Order', section: 'design' },
    { path: '/services', label: 'Services', section: 'services' },
    { path: '/events', label: 'Events', section: 'events' },
    { path: '/gallery', label: 'Gallery', section: 'gallery' },
    { path: '/about', label: 'About', section: 'about' },
    { path: '/contact', label: 'Contact', section: 'contact' },
    // Prospect preview tabs (2026-09-21) - only shown once a valid presentationAccessKey has been
    // entered/arrived via ?key=, see PortalContext.js's hasPreviewAccess.
    ...(hasPreviewAccess ? [
      { path: '/dashboard', label: 'Dashboard', always: true },
      { path: '/presentation', label: 'Presentation', always: true },
    ] : []),
    isAuthenticated
      ? { path: '/account/orders', label: 'My Account', always: true }
      : { path: '/login', label: 'Log In', always: true },
  ];

  const visibleLinks = navLinks.filter(link =>
    link.always || isSectionVisible(link.section)
  );

  return (
    <header style={styles.header}>
      <div style={styles.container}>
        <Link to="/" style={styles.logoLink}>
          {branding.logoUrl ? (
            <span className="logo-flashy-wrap">
              <img
                src={branding.logoUrl}
                alt={branding.businessName}
                style={styles.logo}
              />
            </span>
          ) : (
            <span style={{ ...styles.logoText, color: branding.primaryColor }}>
              {branding.businessName}
            </span>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="site-nav">
          {visibleLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              style={styles.navLink}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="mobile-menu-btn"
          style={styles.mobileMenuBtnBase}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav className="mobile-nav-open" style={styles.mobileNav}>
          {visibleLinks.map(link => (
            <Link 
              key={link.path} 
              to={link.path} 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

const styles = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    background: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '15px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoLink: {
    textDecoration: 'none',
  },
  logo: {
    height: 'clamp(38px, 8vw, 50px)',
    width: 'auto',
  },
  logoText: {
    fontSize: '1.5rem',
    fontWeight: '700',
  },
  navLink: {
    textDecoration: 'none',
    color: '#333',
    fontWeight: '500',
    transition: 'color 0.3s',
  },
  mobileMenuBtnBase: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    margin: '-10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileNav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    borderTop: '1px solid #eee',
  },
  mobileNavLink: {
    padding: '15px 0',
    textDecoration: 'none',
    color: '#333',
    fontWeight: '500',
    borderBottom: '1px solid #eee',
  },
};

export default Header;
